import { Article, Category, Tag, ArticleResItem, CategoryResItem, TagResItem } from './schema';
import { sampleSize } from './helperUtils';
import getConfig from 'next/config';

async function fetchQuery(query: string) {
  const { publicRuntimeConfig } = getConfig();
  const { CONTENTFUL_SPACE_ID, CONTENTFUL_ACCESS_TOKEN, CONTENTFUL_ENVIRONMENT } = publicRuntimeConfig;
  const res = await fetch(
    `https://graphql.contentful.com/content/v1/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${CONTENTFUL_ACCESS_TOKEN}`,
      },
      body: JSON.stringify({ query, variables: { preview: false } }),
    }
  );
  const resJson = await res.json();
  return resJson;
}

export async function loadCategories(): Promise<Category[]> {
  const res = await fetchQuery(Category.getQuery());
  const categories = res?.data?.categoryCollection?.items?.map((i: CategoryResItem) => new Category(i));
  return categories;
}

export async function loadTag(slug: string): Promise<Tag> {
  const res = await fetchQuery(Tag.getQuery({ slug }));
  const tag = res?.data?.tagCollection?.items?.map((i: TagResItem) => new Tag(i))[0];

  return tag;
}

export interface LoadArticlesParams {
  category?: Pick<Category, 'slug'>;
  tags?: Pick<Tag, 'slug'>[];
  exclude?: Article;
  limit?: number;
  skip?: number;
}

export async function loadArticles({ category, tags, exclude, limit, skip }: LoadArticlesParams = {}): Promise<
  Article[]
> {
  if (tags && !tags.length) return [];

  const res = await fetchQuery(
    Article.getQuery({
      category: category?.slug,
      tags: tags?.map((tag) => tag.slug),
      exclude: exclude?.id,
      limit,
      skip,
    })
  );
  const articles = res?.data?.articleCollection?.items || [];
  return articles.length ? articles.map((article: ArticleResItem) => new Article(article)) : [];
}

export async function loadReadMoreArticles(article: Article, relatedArticles: Article[]): Promise<Article[]> {
  const relatedArticleIds = relatedArticles.map((article) => article.id);
  const articles = await loadArticles({ tags: article.tags, exclude: article, limit: 20 });
  const filtered = articles.filter((a: Article) => !relatedArticleIds.includes(a.id));
  return sampleSize(filtered, 2);
}

export async function loadArticle(slug: string): Promise<Article> {
  const res = await fetchQuery(Article.getQuery({ slug, limit: 1 }));
  const article = res?.data?.articleCollection?.items[0];
  return new Article(article);
}
